// Client wrappers for the final roadmap features: bulk tenant actions,
// invoice reconciliation, integration health, consent audit, abuse guard.
import { call } from './callable';


// ── 9. Bulk tenant actions ───────────────────────────────────────────────────
export type BulkAction = 'tag' | 'untag' | 'suspend' | 'resume' | 'export';

export interface BulkResult {
  tenantId: string;
  ok: boolean;
  error?: string;
  skipped?: string;
  row?: Record<string, string | number>;
}

const padminTenantsC = call<any, any>('padminTenants');
const padminBillingC = call<any, any>('padminBilling');
const padminOpsC = call<any, any>('padminOps');
const padminGrowthC = call<any, any>('padminGrowth');

export interface BulkTenantActionResponse {
  total: number;
  succeeded: number;
  failed: number;
  results: BulkResult[];
  rows: Record<string, string | number>[];
}

export const bulkTenantAction = async (p: { tenantIds: string[]; action: BulkAction; tag?: string; reason?: string }): Promise<BulkTenantActionResponse> =>
  (await padminTenantsC({ action: 'bulkTenantAction', tenantIds: p.tenantIds, bulkAction: p.action, tag: p.tag, reason: p.reason })).data;
export const listTags = async () => (await padminTenantsC({ action: 'listTags' })).data.tags;

/**
 * Turn the export rows into a CSV the operator can download.
 *
 * Tenant names are self-signup and attacker-controlled, so a cell starting with
 * = + - @ (or a tab/CR) is prefixed with an apostrophe: Excel and Sheets would
 * otherwise execute it as a formula when the operator opens the export.
 * Quoting alone does not prevent this — the quotes are stripped before parsing.
 */
export function rowsToCsv(rows: Record<string, string | number>[]): string {
  if (rows.length === 0) return '';
  const headers = Object.keys(rows[0]);
  const esc = (v: unknown) => {
    let s = String(v ?? '');
    if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [headers.join(','), ...rows.map((r) => headers.map((h) => esc(r[h])).join(','))].join('\n');
}

export function downloadCsv(filename: string, csv: string): void {
  // BOM so Excel reads the Arabic tenant names as UTF-8.
  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  // Firefox/Safari need the anchor in the document for `download` to fire, and
  // revoking synchronously can abort the still-async save.
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ── 27. Reconciliation ───────────────────────────────────────────────────────
export interface ReconcileIssue {
  kind: 'stuck_pending' | 'paid_no_ref' | 'duplicate_paid' | 'plan_mismatch';
  tenantId: string; tenantName: string; invoiceId: string;
  amountUsd: number; createdAt: number; detail: string;
}

export interface ReconcileReport {
  windowDays: number;
  totals: { invoices: number; paid: number; pending: number; failed: number; collectedUsd: number };
  issues: ReconcileIssue[];
}

export const reconcileInvoices = async (days?: number): Promise<ReconcileReport> =>
  (await padminBillingC({ action: 'reconcileInvoices', days })).data;

// ── 37. Integration health ───────────────────────────────────────────────────
export interface IntegrationRow {
  tenantId: string;
  tenantName: string;
  webhook: {
    configured: boolean; enabled: boolean; events: number;
    lastOk: boolean | null; lastStatus: number | null;
    lastError: string | null; lastDeliveryAt: number | null;
  };
  apiKeys: { total: number; live: number };
  automations: { autoReply: boolean; aiAgent: boolean; chatWidget: boolean };
  severity: number;
}

export const integrationHealth = async (): Promise<{
  rows: IntegrationRow[];
  summary: { webhooksConfigured: number; webhooksFailing: number; apiConsumers: number };
}> => (await padminOpsC({ action: 'integrationHealth' })).data;

// ── 42. Consent audit ────────────────────────────────────────────────────────
export interface ConsentReport {
  subscriber: {
    id: string; phoneE164: string; name: string | null; source: string | null;
    labels: string[]; createdAt: number; deleted: boolean;
  };
  consent: {
    status: string | null; marketingOptIn: boolean | null;
    optedInAt: number | null; optedOutAt: number | null;
    optOutSource: string | null;
    lastEvent: { kind: string; at: number } | null;
    summary: string;
  };
  conversation: { exists: boolean; lastMessageAt: number | null };
  keywordEvents: { id: string; direction: string; text: string; createdAt: number }[];
}

export const consentAudit = async (tenantId: string, phone: string): Promise<ConsentReport> =>
  (await padminGrowthC({ action: 'consentAudit', tenantId, phone })).data;

// ── 35. Abuse guard ──────────────────────────────────────────────────────────
export interface AbuseFlag {
  id: string;
  tenantId: string;
  tenantName: string;
  level: 'flag' | 'throttle';
  score: number;
  reasons: string[];
  signals: Record<string, number | string | null>;
  acknowledged: boolean;
  firstFlaggedAt: number;
  updatedAt: number;
}

export const listAbuseFlags = async (): Promise<{
  flags: AbuseFlag[];
  counts: { throttle: number; flag: number; unacknowledged: number };
}> => (await padminGrowthC({ action: 'listAbuseFlags' })).data;
export const resolveAbuseFlag = async (p: { tenantId: string; action: 'acknowledge' | 'clear'; reason: string }) =>
  (await padminGrowthC({ action: 'resolveAbuseFlag', tenantId: p.tenantId, subAction: p.action, reason: p.reason })).data;
export interface AbuseScanResult {
  tenantId: string;
  verdict: {
    level: 'ok' | 'flag' | 'throttle';
    score: number;
    reasons: string[];
    signals: Record<string, number | string | null>;
  };
  dryRun?: boolean;
}

export const scanTenantAbuse = async (tenantId: string): Promise<AbuseScanResult> =>
  (await padminGrowthC({ action: 'scanTenantAbuse', tenantId })).data;
